import Button, { LinkButton } from '@components/shared/Button'
import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
  KeyIcon,
  UserCircleIcon,
} from '@heroicons/react/20/solid'
import { useWallet } from '@solana/wallet-adapter-react'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import { NextPage } from 'next'
import Head from 'next/head'
import { useMemo, useState, ReactNode } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'next-i18next'
import { useProfileDetails } from '@components/profile/useProfileDetails'
import EditProfileModal from '@components/profile/EditProfileModal'
import Faqs from '@components/partners/Faqs'
import { DISCORD_LINK } from 'utils/constants'
import ConnectButton from '@components/shared/ConnectButton'
import ActivatePartnerModal from '@components/partners/ActivatePartnerModal'
import BeachballLoader from '@components/shared/BeachballLoader'
import { differenceInDays, addDays } from 'date-fns'
import {
  calculateFee,
  getNextFeeRate,
} from '@components/partners/useReferrerFee'
import CountdownClock from '@components/shared/CountdownClock'
import MedalIcon from '@components/icons/MedalIcon'
import { useIsPartnerWhitelisted } from '@components/partners/useIsPartnerWhitelisted'
import HeaderBanner from '@components/partners/HeaderBanner'
import ButtonLink from '@components/shared/ButtonLink'
import { withCommonNamespaces } from 'utils/common-namespaces'
import { ExternalLink } from '@components/shared/ExternalLink'

interface HowItWorks {
  icon: ReactNode
  title: string
  description: string
  isActive: boolean
  isCompleted: boolean
  button?: {
    text: string
    onClick: () => void
  }
}

const metaTitle = 'Partner Program | Cabana'

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(
        locale,
        withCommonNamespaces(['partners', 'partners-faqs']),
      )),
      metaTitle,
    },
  }
}

const Referrals: NextPage = () => {
  const { t } = useTranslation('partners')
  const { connected } = useWallet()
  const { isWhitelisted } = useIsPartnerWhitelisted()
  const [showEditProfileModal, setShowEditProfileModal] = useState(false)
  const [showActivatePartnerModal, setShowActivatePartnerModal] =
    useState(false)
  const { isCopied, handleCopyAddress } = useCopyToClipboard()
  const { profileDetails, loadingProfileDetails, isFetchingProfileDetails } =
    useProfileDetails()
  const ownedReferralLink = profileDetails?.username
    ? `${window.location.origin}/?partner=${encodeURIComponent(
        profileDetails.username,
      )}`
    : ''

  const howItWorksList: HowItWorks[] = useMemo(
    () => [
      {
        icon: <UserCircleIcon className="mb-2 size-9 text-th-up" />,
        title: t('how_it_works.create_profile.title'),
        description: t('how_it_works.create_profile.description'),
        button: {
          text: t('how_it_works.create_profile.button'),
          onClick: () => {
            setShowEditProfileModal(true)
          },
        },
        isActive: true,
        isCompleted: !!profileDetails?.username,
      },
      {
        icon: <KeyIcon className="mb-2 size-9 text-th-up" />,
        title: t('how_it_works.activate.title'),
        description: t('how_it_works.activate.description'),
        button: {
          text: t('how_it_works.activate.button'),
          onClick: () => {
            setShowActivatePartnerModal(true)
          },
        },
        isActive: !!profileDetails?.username,
        isCompleted: profileDetails?.partnerStartAt != null,
      },
    ],
    [profileDetails, t],
  )

  const currentPartnerFee = useMemo(() => {
    if (!profileDetails?.partnerStartAt) return 0
    const daysSinceStart = differenceInDays(
      new Date(),
      profileDetails.partnerStartAt,
    )
    return calculateFee(daysSinceStart)
  }, [profileDetails])

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      {loadingProfileDetails || isFetchingProfileDetails ? (
        <div className="flex h-[calc(100vh-256px)] items-center justify-center">
          <BeachballLoader className="size-8" />
        </div>
      ) : !connected ? (
        <>
          <HeaderBanner />
          <div className="mx-auto mt-8 flex max-w-2xl flex-col items-center text-center">
            <p className="mb-1 font-bold uppercase text-th-button md:text-base">
              {t('header.subtitle')}
            </p>
            <h1 className="mb-2 md:text-4xl">{t('header.title')}</h1>
          </div>
          <div className="mx-auto mt-6 flex w-full max-w-2xl flex-col items-center rounded-xl border border-th-bkg-3 p-4 md:p-6">
            <h2 className="mb-6 text-center text-lg">{t('connect.title')}</h2>
            <ConnectButton />
          </div>
        </>
      ) : !isWhitelisted ? (
        <>
          <HeaderBanner />
          <div className="mx-auto max-w-2xl py-8 text-center lg:py-12">
            <p className="mb-1 font-bold uppercase text-th-button md:text-base">
              {t('become_partner.subtitle')}
            </p>
            <h1 className="mb-2 md:text-4xl">{t('become_partner.title')}</h1>
            <p className="text-base text-th-fgd-1 md:text-lg">
              {t('become_partner.description')}
            </p>
            <ButtonLink
              path="https://docs.google.com/forms/d/e/1FAIpQLSdtTKWwXL2475kPs0Z7tIOBFHae02IRsQIm76DeF4iDA4Emaw/viewform?usp=header"
              className="mx-auto mt-6"
            >
              {t('become_partner.button')}
            </ButtonLink>
          </div>
        </>
      ) : !profileDetails?.partnerStartAt ? (
        <>
          <div className="mx-auto max-w-2xl py-8 text-center lg:py-12">
            <p className="mb-1 font-bold uppercase text-th-button md:text-base">
              {t('howdy_partner.subtitle')}
            </p>
            <h1 className="mb-2 md:text-4xl">{t('howdy_partner.title')}</h1>
            <p className="mb-2 text-base text-th-fgd-1 md:text-lg">
              {t('howdy_partner.congrats')}
            </p>
            <p className="md:text-base">{t('howdy_partner.description')}</p>
          </div>
          <h2 className="mb-6 text-center md:text-2xl">
            {t('start_earning.title')}
          </h2>
          <div className={`grid grid-cols-2 gap-4 pb-12 lg:gap-6`}>
            {howItWorksList.map(
              (
                { icon, title, description, button, isActive, isCompleted },
                index,
              ) => (
                <div
                  className={`group/number relative col-span-2 rounded-lg bg-th-bkg-2 p-4 md:col-span-1 md:p-6 ${
                    isActive ? '' : 'opacity-50'
                  }`}
                  key={title}
                >
                  <div className="mb-3 flex justify-between">
                    {icon}
                    <span className="text-4xl font-medium text-th-fgd-4 transition-all delay-75 group-hover/number:text-th-fgd-3">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mb-2 md:text-lg">{title}</h3>
                  <p className="text-base text-th-fgd-2">{description}</p>
                  {!connected && isActive ? (
                    <div className="mt-6">
                      <ConnectButton />
                    </div>
                  ) : button ? (
                    <div className="mt-6">
                      <Button
                        onClick={button.onClick}
                        disabled={!isActive || isCompleted}
                      >
                        {button.text}
                      </Button>
                    </div>
                  ) : null}
                  {isCompleted ? (
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                      <div className="absolute inset-0 rounded-lg bg-th-bkg-2 opacity-80" />
                      <CheckCircleIcon className="z-10 size-10 text-th-up" />
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </div>
          <h2 className="mb-1 text-center md:text-2xl">{t('faqs.title')}</h2>
          <p className="mb-6 text-center text-base text-th-fgd-2">
            <Trans
              t={t}
              i18nKey="faqs.discord_text"
              components={{
                discordLink: (
                  <ExternalLink
                    className="text-th-active"
                    href={DISCORD_LINK}
                  />
                ),
              }}
            />
          </p>
          <Faqs />
        </>
      ) : (
        <div className="mx-auto max-w-2xl">
          <h1 className="mb-6 text-xl">{t('dashboard.title')}</h1>
          <div className="space-y-8">
            <div>
              <h2 className="mb-1 text-lg">
                {t('dashboard.partner_link.title')}
              </h2>
              <p className="mb-4">{t('dashboard.partner_link.description')}</p>
              <div className="rounded-xl bg-th-bkg-3 p-6">
                <div className="flex items-center justify-between space-x-4">
                  <p className="truncate whitespace-nowrap text-th-fgd-1 md:text-base">
                    {ownedReferralLink}
                  </p>
                  <LinkButton
                    className="mr-1 flex items-center gap-2 whitespace-nowrap text-th-fgd-2"
                    onClick={() => handleCopyAddress(ownedReferralLink)}
                  >
                    {isCopied ? (
                      <>
                        <span>{t('dashboard.partner_link.copied')}</span>
                        <CheckCircleIcon className="size-6 text-th-up" />
                      </>
                    ) : (
                      <>
                        <span>{t('dashboard.partner_link.copy')}</span>
                        <DocumentDuplicateIcon className="size-6" />
                      </>
                    )}
                  </LinkButton>
                </div>
              </div>
            </div>
            <div>
              <div className="flex flex-col items-center rounded-xl border border-th-bkg-4 p-4 md:p-6">
                <div className="relative mb-3 flex size-16 shrink-0 items-center justify-center">
                  <MedalIcon className="absolute size-20" rank={1} />
                  <div className="absolute size-14 rounded-full bg-white/40" />
                  <p className="relative z-10 text-xl font-bold text-[#01070E]">
                    {currentPartnerFee}%
                  </p>
                </div>
                <h2 className="mb-1 text-lg">
                  {t('dashboard.referral_rate.title')}
                </h2>
                <p className="mb-4 text-center">
                  {t('dashboard.referral_rate.description', {
                    rate: currentPartnerFee,
                  })}
                </p>
                <CountdownClock
                  countdownFinishedMessage={
                    <div className="text-sm text-th-fgd-2">
                      {t(
                        'dashboard.referral_rate.countdown.final_rate_reached',
                      )}
                    </div>
                  }
                  countdownTo={(now: Date) => {
                    if (!profileDetails?.partnerStartAt) {
                      return null
                    }

                    const daysSinceStart = differenceInDays(
                      now,
                      profileDetails.partnerStartAt,
                    )

                    if (daysSinceStart > 60) {
                      return null
                    }

                    let nextMilestone: Date
                    if (daysSinceStart < 30) {
                      nextMilestone = addDays(profileDetails.partnerStartAt, 30)
                    } else {
                      nextMilestone = addDays(profileDetails.partnerStartAt, 60)
                    }

                    return nextMilestone
                  }}
                />
                {currentPartnerFee !== 25 ? (
                  <div className="mt-3 flex w-full flex-col items-center border-t border-th-bkg-3 pt-3 text-center">
                    <p className="mb-1 mt-2 text-th-fgd-1">
                      {t('dashboard.referral_rate.countdown.text')}
                    </p>
                    <p className="text-xs">
                      {t('dashboard.referral_rate.countdown.next_rate', {
                        rate: getNextFeeRate(currentPartnerFee),
                      })}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="rounded-xl border border-th-bkg-4 p-4 md:p-6">
              <h2 className="mb-1 text-lg">{t('dashboard.earnings.title')}</h2>
              <p>{t('dashboard.earnings.coming_soon')}</p>
            </div>
            <div>
              <h2 className="mb-1 text-center text-lg">{t('faqs.title')}</h2>
              <p className="mb-6 text-center text-th-fgd-2">
                <Trans
                  t={t}
                  i18nKey="faqs.discord_text"
                  components={{
                    discordLink: (
                      <ExternalLink
                        className="text-th-active"
                        href={DISCORD_LINK}
                      />
                    ),
                  }}
                />
              </p>
              <Faqs />
            </div>
          </div>
        </div>
      )}
      {showEditProfileModal ? (
        <EditProfileModal
          isOpen={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
        />
      ) : null}
      {showActivatePartnerModal ? (
        <ActivatePartnerModal
          isOpen={showActivatePartnerModal}
          onClose={() => setShowActivatePartnerModal(false)}
        />
      ) : null}
    </>
  )
}

export default Referrals
